import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Grid, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { tabsClasses } from '@mui/material/Tabs';

import { ROUTE_CUSTOMERS } from '../../../../core/constants';
import { ReturnPageButton } from '../../../common/button';
import { getCustomerAction, getCustomerState } from '../../../../features/customer/get_customer';
import { getCustomerStatus, getCustomerStatusColor } from '../../../common/utils/customer_utils';
import useNotification from '../../../../core/hooks/use_notification';
import { getCustomerNotificationData } from '../../../../core/utils/notification_utils';

import CustomerDetails from './tabs/details/customer_details';
import ActivitiesList from './tabs/activities/activities_list';
import TheftRegistriesList from './tabs/theft_registries/theft_registries_list';
import TransactionsList from './tabs/transactions/transactions_list';
import ReservationsList from './tabs/reservation/reservations_list';
import CustomerBlockadeRegistryList from './tabs/blockade_registries';
import CustomerBlockadeHistoryRegistryList from './tabs/blockade_history_registries';

const CustomerPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { customerId } = useParams();
    const location = useLocation();
    const { showErrorMessage } = useNotification();

    const [tab, setTab] = useState(location?.state?.tab || 0);

    const getCustomer = useSelector(getCustomerState);

    useEffect(() => {
        if (customerId) {
            dispatch(getCustomerAction(customerId))
                .then(response => showErrorMessage(getCustomerNotificationData(response)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, customerId]);

    const renderView = () => {
        if (tab === 0) return <CustomerDetails customerId={ customerId }/>;
        if (tab === 1) return <ActivitiesList customerId={ customerId }/>;
        if (tab === 2) return <TransactionsList customerId={ customerId } tab={ tab }/>;
        if (tab === 3) return <TheftRegistriesList customerId={ customerId } tab={ tab }/>;
        if (tab === 4) return <ReservationsList customerId={ customerId } tab={ tab }/>;
        if (tab === 5) return <CustomerBlockadeRegistryList customerId={ customerId }/>;
        if (tab === 6) return <CustomerBlockadeHistoryRegistryList customerId={ customerId }/>;
        return null;
    };

    const customerData = getCustomer?.data;

    return (
        <Box mb={ 6 }>
            { getCustomer?.loading && <LinearProgress sx={ { mb: 2 } }/> }

            { customerData && (
                <Grid container rowGap={ 3 }>

                    <Grid item xs={ 12 }>
                        <Stack direction="row" alignItems="center" flexWrap="wrap">
                            <Stack direction="row" alignItems="center" spacing={ 2 }>
                                <ReturnPageButton
                                    onClick={ () => navigate(ROUTE_CUSTOMERS, { state: { queryParams: location?.state?.queryParams } }) }/>
                                <Typography variant="h4" component="h1" sx={ { fontWeight: 600 } }>
                                    { [customerData.name, customerData.surname].filter(Boolean).join(' ') }
                                </Typography>
                                <Typography variant="h6" color="textSecondary">
                                    ID: { customerData.id }
                                </Typography>
                                <Chip
                                    label={ getCustomerStatus(customerData.status) }
                                    color={ getCustomerStatusColor(customerData.status) }
                                    sx={ { fontWeight: 600 } }
                                />
                            </Stack>
                        </Stack>
                    </Grid>


                    <Grid item xs={ 12 }>
                        <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                            <Tabs
                                value={ tab }
                                onChange={ (_, value) => setTab(value) }
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={ {
                                    [`& .${ tabsClasses.scrollButtons }`]: {
                                        '&.Mui-disabled': { opacity: 0.3 },
                                    },
                                } }
                            >
                                <Tab label="Informacje ogólne"/>
                                <Tab label="Aktywności"/>
                                <Tab label="Zakupy stacjonarne"/>
                                <Tab label="Kartoteka"/>
                                <Tab label="Zamówienia"/>
                                <Tab label="Aktualne blokady"/>
                                <Tab label="Rejestr blokad"/>
                            </Tabs>
                        </Box>
                    </Grid>


                    <Grid item xs={ 12 }>
                        <Box sx={ { py: 2 } }>
                            { renderView() }
                        </Box>
                    </Grid>

                </Grid>
            ) }
        </Box>
    );
};

export default CustomerPage;